// Local persistence. Spec section 5.
// Stores ONLY onboarding state, language and manually recorded sessions.
// Never store diagnosis, symptoms, medication, medical history, free-text
// health data or personal identity (spec: privacy by design).

export interface SessionRecord {
  id: string;          // ISO timestamp used as id
  completedAt: string; // ISO timestamp (UTC instant)
  localDate: string;   // YYYY-MM-DD in the user's local timezone at record time
  source: "manual";
}

export interface AppState {
  version: 1;
  onboardingCompleted: boolean;
  language: string;
  sessions: SessionRecord[];
}

export const STORAGE_KEY = "viraball_companion_v1";

const defaultState: AppState = {
  version: 1,
  onboardingCompleted: false,
  language: "en",
  sessions: []
};

function isValidState(value: unknown): value is AppState {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    v.version === 1 &&
    typeof v.onboardingCompleted === "boolean" &&
    typeof v.language === "string" &&
    Array.isArray(v.sessions)
  );
}

export function getAppState(): AppState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...defaultState, sessions: [] };
    const parsed: unknown = JSON.parse(raw);
    if (!isValidState(parsed)) return { ...defaultState, sessions: [] };
    return parsed;
  } catch {
    // Corrupted storage or storage unavailable: fall back safely.
    return { ...defaultState, sessions: [] };
  }
}

export function saveAppState(state: AppState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage may be full or blocked (e.g. private mode). App must not crash.
  }
}

export function completeOnboarding(): AppState {
  const state = getAppState();
  state.onboardingCompleted = true;
  saveAppState(state);
  return state;
}

/** Format a Date as YYYY-MM-DD using LOCAL calendar fields (never UTC). */
export function toLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function recordSession(now: Date = new Date()): AppState {
  const state = getAppState();
  const iso = now.toISOString();
  state.sessions.push({
    id: iso,
    completedAt: iso,
    localDate: toLocalDate(now),
    source: "manual"
  });
  saveAppState(state);
  return state;
}

export function resetAppData(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
